import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { Train, MapPin, ArrowRight, Route as RouteIcon, QrCode, Plus, Smartphone } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useTripCart } from "@/lib/tripCart";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tailwind can't lazy-generate class names — enumerate accent colors.
const ACCENTS = {
  amber:   { text: "text-amber-400",   ring: "hover:border-amber-500/40",   dot: "bg-amber-500",   bar: "from-amber-500/25 to-transparent" },
  blue:    { text: "text-sky-400",     ring: "hover:border-sky-500/40",     dot: "bg-sky-500",     bar: "from-sky-500/25 to-transparent" },
  emerald: { text: "text-emerald-400", ring: "hover:border-emerald-500/40", dot: "bg-emerald-500", bar: "from-emerald-500/25 to-transparent" },
  purple:  { text: "text-purple-400",  ring: "hover:border-purple-500/40",  dot: "bg-purple-500",  bar: "from-purple-500/25 to-transparent" },
};

export function FeaturedTrips() {
  const [trips, setTrips] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { setItems } = useTripCart();
  const [qrTrip, setQrTrip] = useState(null); // trip whose QR modal is open

  const startCustomTrip = () => {
    // Keep any existing selections — don't nuke the user's cart. Just navigate.
    navigate("/trip-planner");
  };

  const shareableUrl = (trip) => {
    if (!trip) return "";
    return `${window.location.origin}/trip-planner?stops=${trip.depot_ids.join(",")}`;
  };

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/trips/featured`)
      .then((r) => {
        if (!cancelled) setTrips(r.data || []);
      })
      .catch(() => {
        if (!cancelled) setTrips([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <section className="py-12 bg-[#0a0a0a] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-4">
          <div className="h-6 w-64 bg-white/5 rounded animate-pulse mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[0, 1, 2].map((i) => (
              <div key={i} className="h-52 bg-white/[0.03] rounded-xl animate-pulse" />
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (!trips) return null; // still loading state above; we already returned skeleton

  const startTrip = (trip) => {
    const stops = trip.stops.map((s) => ({
      id: s.id,
      name: s.name,
      city: s.city,
      state_code: s.state_code,
      latitude: s.latitude,
      longitude: s.longitude,
    }));
    setItems(stops);
    toast.success(`Loaded ${trip.title} · ${trip.stop_count} stops`);
    navigate("/trip-planner");
  };

  return (
    <section
      className="py-14 bg-[#0a0a0a] border-t border-white/5"
      data-testid="featured-trips-section"
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className="flex items-baseline justify-between mb-8 flex-wrap gap-3">
          <div>
            <p className="text-[11px] uppercase tracking-[0.32em] text-amber-500 mb-2 flex items-center gap-2">
              <RouteIcon className="w-3.5 h-3.5" />
              Featured Trips
            </p>
            <h2 className="font-serif text-3xl font-bold text-white">
              Pre-planned depot road trips
            </h2>
            <p className="text-sm text-white/50 mt-1.5 max-w-xl">
              One tap loads a curated multi-stop route into the trip planner —
              customize, optimize, and share to your phone.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {trips.map((trip) => {
            const a = ACCENTS[trip.accent] || ACCENTS.amber;
            return (
              <button
                key={trip.slug}
                type="button"
                onClick={() => startTrip(trip)}
                className={`group relative overflow-hidden text-left rounded-xl bg-[#111] border border-white/5 ${a.ring} p-5 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-black/40`}
                data-testid={`featured-trip-${trip.slug}`}
              >
                {/* Decorative accent bar */}
                <div
                  className={`absolute top-0 left-0 right-0 h-24 bg-gradient-to-b ${a.bar} pointer-events-none opacity-70`}
                  aria-hidden="true"
                />

                <div className="relative">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${a.dot}`} />
                    <span className={`text-[10px] uppercase tracking-[0.2em] ${a.text} font-semibold`}>
                      {trip.cover_state_codes.slice(0, 4).join(" · ")}
                    </span>
                  </div>
                  <h3 className="font-serif text-xl font-semibold text-white leading-tight mb-1">
                    {trip.title}
                  </h3>
                  <p className="text-sm text-white/60 mb-3">{trip.subtitle}</p>
                  <p className="text-sm text-white/70 leading-relaxed mb-4 line-clamp-3">
                    {trip.description}
                  </p>

                  <div className="flex items-baseline justify-between gap-3 pt-3 border-t border-white/5">
                    <div className="flex items-center gap-4 text-xs text-white/50 font-mono">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {trip.stop_count} stops
                      </span>
                      <span className="flex items-center gap-1">
                        <Train className="w-3 h-3" />
                        {Math.round(trip.total_miles_crow_flies).toLocaleString()} mi
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                          e.stopPropagation();
                          setQrTrip(trip);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            e.stopPropagation();
                            setQrTrip(trip);
                          }
                        }}
                        title="Scan on your phone"
                        className={`inline-flex items-center gap-1 text-xs text-white/50 hover:${a.text} transition-colors cursor-pointer p-1 -m-1 rounded`}
                        data-testid={`featured-trip-qr-${trip.slug}`}
                      >
                        <QrCode className="w-3.5 h-3.5" />
                      </span>
                      <span className={`text-xs font-semibold ${a.text} inline-flex items-center gap-1 group-hover:gap-2 transition-all`}>
                        Plan Trip
                        <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    </div>
                  </div>
                </div>
              </button>
            );
          })}

          {/* Build-your-own custom trip card */}
          <button
            type="button"
            onClick={startCustomTrip}
            className="group relative overflow-hidden text-left rounded-xl bg-gradient-to-br from-amber-500/[0.08] via-orange-500/[0.05] to-transparent border border-dashed border-amber-500/30 hover:border-amber-500/60 hover:from-amber-500/[0.14] p-5 transition-all hover:-translate-y-0.5 hover:shadow-xl hover:shadow-amber-500/10"
            data-testid="featured-trip-custom"
          >
            <div className="relative">
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500 text-black">
                  <Plus className="w-3.5 h-3.5" strokeWidth={3} />
                </span>
                <span className="text-[10px] uppercase tracking-[0.2em] text-amber-400 font-semibold">
                  Build Your Own
                </span>
              </div>
              <h3 className="font-serif text-xl font-semibold text-white leading-tight mb-1">
                Custom Depot Road Trip
              </h3>
              <p className="text-sm text-amber-300/80 mb-3">
                Pick your stops · optimize · send to your phone
              </p>
              <p className="text-sm text-white/70 leading-relaxed mb-4">
                Search or tap any depot on the map to add it to your trip. We&apos;ll
                find the shortest route and give you a QR code to open turn-by-turn
                directions on your phone.
              </p>

              <div className="flex items-center justify-between gap-3 pt-3 border-t border-amber-500/20">
                <div className="flex items-center gap-3 text-xs text-white/60 font-mono">
                  <span className="flex items-center gap-1">
                    <RouteIcon className="w-3 h-3 text-amber-400" />
                    Up to 25 stops
                  </span>
                  <span className="flex items-center gap-1">
                    <QrCode className="w-3 h-3 text-amber-400" />
                    QR to phone
                  </span>
                </div>
                <span className="text-xs font-semibold text-amber-400 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                  Start Planning
                  <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* QR modal for a featured trip — scan to open on phone */}
      <Dialog open={!!qrTrip} onOpenChange={(open) => !open && setQrTrip(null)}>
        <DialogContent className="sm:max-w-sm bg-[#1a1a1a] border-white/10" data-testid="featured-trip-qr-dialog">
          <DialogHeader>
            <DialogTitle className="text-white font-serif">
              {qrTrip?.title}
            </DialogTitle>
            <DialogDescription className="text-white/60 text-xs">
              {qrTrip?.stop_count} stops · {qrTrip ? Math.round(qrTrip.total_miles_crow_flies).toLocaleString() : ""} mi
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center py-3">
            <div className="bg-white p-3 rounded-lg shadow-lg shadow-black/40">
              {qrTrip && (
                <QRCodeSVG
                  value={shareableUrl(qrTrip)}
                  size={200}
                  level="M"
                  includeMargin={false}
                  fgColor="#0a0a0a"
                  bgColor="#ffffff"
                />
              )}
            </div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-amber-500 mt-4 flex items-center gap-1.5">
              <Smartphone className="w-3.5 h-3.5" />
              Scan with your phone
            </p>
            <p className="text-sm text-white/70 mt-2 text-center px-3 leading-relaxed">
              Opens the trip planner on your device with all stops preloaded.
              Ready to optimize and get turn-by-turn directions.
            </p>
            <div className="flex gap-2 mt-4">
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard?.writeText(shareableUrl(qrTrip));
                  toast.success("Trip link copied");
                }}
                className="text-xs px-3 py-1.5 rounded-md bg-white/5 border border-white/10 text-white/85 hover:bg-white/10 hover:border-amber-500/40"
                data-testid="featured-trip-copy-link"
              >
                Copy link
              </button>
              <button
                type="button"
                onClick={() => {
                  qrTrip && startTrip(qrTrip);
                  setQrTrip(null);
                }}
                className="text-xs px-3 py-1.5 rounded-md bg-amber-500 text-black font-semibold hover:bg-amber-600"
              >
                Open here instead
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </section>
  );
}
